/**
 * Ad-supported account play: pure policy + state machine.
 *
 * Policy (account matches only; guest private tables are never gated):
 * - The first account match is free of ads (server flag
 *   `adFreeMatchAvailable`, derived from the account's match-entry ledger).
 * - After that, each account play attempt on iOS needs one interstitial that
 *   was actually displayed and then dismissed. One completed ad unlocks one
 *   attempt; the unlock is kept until that attempt succeeds, so a network
 *   error starting the table never costs a second ad.
 * - Web and Android never require an ad (no native ads on web; Android is out
 *   of scope for this policy). Coins entry fees still apply everywhere.
 * - If the ad cannot load/show, the player may retry or continue this one
 *   attempt without an ad. The gate can never lock play forever.
 */
import type { AdPlatform, InterstitialResult } from './ad-mob';
import { unlocksPlay } from './ad-mob';

export type PlayRequirement = 'none' | 'ad';

export interface PlayPolicyInput {
  readonly platform: AdPlatform;
  /** Server flag; undefined when the wallet is unknown (error/older server). */
  readonly adFreeMatchAvailable: boolean | undefined;
}

export const playRequirement = ({
  platform,
  adFreeMatchAvailable,
}: PlayPolicyInput): PlayRequirement =>
  platform === 'ios' && adFreeMatchAvailable !== true ? 'ad' : 'none';

export type PlayGateState =
  | { readonly kind: 'idle'; readonly unlocked: boolean }
  /** Explaining the ad before anything is requested. */
  | { readonly kind: 'explain' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'showing' }
  | { readonly kind: 'failed'; readonly stage: string }
  /** Ready to run the gated play action (once). */
  | { readonly kind: 'go'; readonly viaAd: boolean };

export type PlayGateAction =
  | { readonly type: 'request'; readonly requirement: PlayRequirement }
  | { readonly type: 'watch' }
  | { readonly type: 'shown' }
  | { readonly type: 'result'; readonly result: InterstitialResult }
  | { readonly type: 'skipAfterFailure' }
  | { readonly type: 'cancel' }
  /** The gated play action was started (go -> idle, keeping the unlock). */
  | { readonly type: 'started' }
  /** The play attempt succeeded: consume the unlock. */
  | { readonly type: 'played' };

export const initialPlayGate: PlayGateState = { kind: 'idle', unlocked: false };

export const playGateReducer = (
  state: PlayGateState,
  action: PlayGateAction
): PlayGateState => {
  switch (action.type) {
    case 'request':
      if (state.kind !== 'idle') return state;
      if (action.requirement === 'none' || state.unlocked) {
        return { kind: 'go', viaAd: state.unlocked };
      }
      return { kind: 'explain' };
    case 'watch':
      return state.kind === 'explain' || state.kind === 'failed'
        ? { kind: 'loading' }
        : state;
    case 'shown':
      return state.kind === 'loading' ? { kind: 'showing' } : state;
    case 'result': {
      if (state.kind !== 'loading' && state.kind !== 'showing') return state;
      if (unlocksPlay(action.result)) return { kind: 'go', viaAd: true };
      return {
        kind: 'failed',
        stage:
          action.result.status === 'failed' ? action.result.stage : 'show',
      };
    }
    case 'skipAfterFailure':
      // Only reachable after a real load/show failure.
      return state.kind === 'failed' ? { kind: 'go', viaAd: false } : state;
    case 'cancel':
      return state.kind === 'explain' || state.kind === 'failed'
        ? { kind: 'idle', unlocked: false }
        : state;
    case 'started':
      // A completed ad stays unlocked until the attempt is confirmed.
      return state.kind === 'go'
        ? { kind: 'idle', unlocked: state.viaAd }
        : state;
    case 'played':
      return { kind: 'idle', unlocked: false };
    default:
      return state;
  }
};

export const isGateBusy = (s: PlayGateState) =>
  s.kind === 'loading' || s.kind === 'showing';
